import { Button, Form } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { gerProfileForLocation } from '../../offices/offices-selectors';
import { capitalize } from 'lodash';
import moment from 'moment';
import { createLocation } from '../../offices/offices-actions';
import { profileConfig } from '../configs/profile-config';
import { getAdminEmails, getWriteTabsPermissions } from '../../../store/user/user-reducer';
import { formItemMap } from '@med-fe/ui';
import { PosEnum } from 'apps/mod/src/models/pos-enum';
import { useMsal } from '@azure/msal-react';
import { fetchAdminEmails } from 'apps/mod/src/store/user/user-actions';

function ProfileDetails({ locId }) {
  const [form] = Form.useForm();
  const [isDisabled, setIsDisabled] = useState(true);
  const dispatch = useDispatch();
  const data = useSelector((state) => gerProfileForLocation(state, locId));
  const isEditAllowed = useSelector(getWriteTabsPermissions).profile;
  const [posValue, setPosValue] = useState(data?.pos ? data?.pos : null);
  const { accounts } = useMsal();
  const adminEmails = useSelector(getAdminEmails);
  const [allowDateClear, setAllowDateClear] = useState<boolean>(false);
  const hasMounted = useRef(false);
  const [openDate, setOpenDate] = useState(data?.openingDate ? data?.openingDate : null);
  const [leaseExpirationDate, setLeaseExpirationDate] = useState(data?.leaseExpiration ? data?.leaseExpiration : null);
  const [encompassDate, setEncompassDate] = useState(data?.encompassLaunchDate ? data?.encompassLaunchDate : null);
  const [isOpenDateConfirmVisible, setOpenDateConfirmVisible] = useState(false);
  const [isleaseExpirationDateConfirmVisible, setleaseExpirationDateConfirmVisible] = useState(false);
  const [isencompassDateConfirmVisible, setencompassDateConfirmVisible] = useState(false);

  useEffect(() => {
    if (hasMounted.current) return;
    hasMounted.current = true;
    dispatch(fetchAdminEmails());
  }, []);

  useEffect(() => {
    setAllowDateClear(adminEmails?.some((user) => user?.trim().toLowerCase() === accounts[0]?.username));
  }, [adminEmails, accounts]);

  useEffect(() => {
    form.setFieldsValue({
      ...data,
      ...(!!data.openingDate && { openingDate: moment(data.openingDate) }),
      ...(!!data.leaseExpiration && { leaseExpiration: moment(data.leaseExpiration) }),
      ...(!!data.encompassLaunchDate && { encompassLaunchDate: moment(data.encompassLaunchDate) }),
    });
  }, [locId]);

  const onSubmit = (formData: any) => {
    const { openingDate, leaseExpiration, modifiedDate, encompassLaunchDate, ...restData } = formData;
    const payload = {
      ...restData,
      ...{ openingDate: openDate },
      ...{ leaseExpiration: leaseExpirationDate },
      ...{ encompassLaunchDate: encompassDate },
      id: locId,
    };
    dispatch(createLocation(payload));
  };

  const onPosSelect = (value) => {
    setPosValue(value);
    if (value === 'ENCOMPASS') {
      form.setFieldsValue({ downstreamIntegration: true });
    }
  };

  const onOpenDateSelect = (value) => {
    form.setFieldsValue({ openingDate: value ? value.format('YYYY-MM-DD') : null });
    setOpenDate(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onLeaseExpirationSelect = (value) => {
    form.setFieldsValue({ leaseExpiration: value ? value.format('YYYY-MM-DD') : null });
    setLeaseExpirationDate(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
  };

  const onEncompassLaunchDateSelect = (value) => {
    form.setFieldsValue({ encompassLaunchDate: value ? value.format('YYYY-MM-DD') : null });
    setEncompassDate(value ? value.format('YYYY-MM-DD') : null);
    setIsDisabled(false);
    form.validateFields(['encompassLaunchDate']);
  };

  const onConfirmSetVisible = (value, name) => {
    if (name == 'Open Date') setOpenDateConfirmVisible(value);
    if (name == 'Lease Expiration') setleaseExpirationDateConfirmVisible(value);
    if (name == 'Encompass Launch Date') setencompassDateConfirmVisible(value);
  };

  return (
    <Form
      form={form}
      layout='vertical'
      className='profile-details-form'
      onFinish={(formData) => {
        onSubmit(formData);
        setIsDisabled(true);
      }}
      onValuesChange={() => setIsDisabled(false)}
    >
      {Object.entries(profileConfig).map(([key, value]: any) => {
        const params = {
          key,
          label: capitalize(value.label),
          name: key,
          rules: value.rules,
          ...(value.type === 'checkbox' && { valuePropName: 'checked' }),
        };
        const formItemParams = {
          ...value,
          ...(key === 'mergedInto' && {
            data: data.mergedInto,
          }),
          ...(key === 'pos' && { onSelect: onPosSelect, data: Object.entries(PosEnum) }),
          ...(key === 'openingDate' && {
            onSelect: onOpenDateSelect,
            data: openDate,
            onConfirmVisible: onConfirmSetVisible,
            isConfirmVisible: isOpenDateConfirmVisible,
            name: value.label,
          }),
          ...(key === 'leaseExpiration' && {
            onSelect: onLeaseExpirationSelect,
            data: leaseExpirationDate,
            onConfirmVisible: onConfirmSetVisible,
            isConfirmVisible: isleaseExpirationDateConfirmVisible,
            name: value.label,
          }),
          ...(key === 'encompassLaunchDate' && {
            onSelect: onEncompassLaunchDateSelect,
            data: encompassDate,
            onConfirmVisible: onConfirmSetVisible,
            isConfirmVisible: isencompassDateConfirmVisible,
            name: value.label,
          }),
          allowDateClear: allowDateClear,
        };
        return !(key == 'encompassLaunchDate' && (posValue === 'ALE' || posValue === null)) ? (
          <Form.Item
            {...params}
            className={value.type === 'checkbox' ? 'checkbox-item' : ''}
            validateTrigger={['onBlur', 'onSubmit']}
          >
            {formItemMap(formItemParams, !isEditAllowed)}
          </Form.Item>
        ) : null;
      })}

      <Form.Item>
        <Button type='primary' htmlType='submit' className={'btn-form-action'} disabled={!isEditAllowed || isDisabled}>
          SAVE
        </Button>
      </Form.Item>
    </Form>
  );
}

export default ProfileDetails;
